import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import EmptyAddCardCst from "@components/EmptyAddCardCst";
import { Stakeholder } from "@components/StakeholdersCst";
import StakeholdersCstRow from "@components/StakeholdersCst/components/StakeholdersCstRow";
import StakeholdersPickerCst from "@components/StakeholdersPickerCst";
import { WizardStepProps } from "@components/WizardCst/types";
import { MediaType } from "src/interfaces/Media";
import { ActivityWizardData } from "../../../types";
import { isFieldVisible } from "../config/additionalInfoConfig";
import SectionHeader from "./SectionHeader";
import MultiAssociationField from "./MultiAssociationField";
import UsefulLinksField from "./UsefulLinksField";

const ToolsSection: React.FC<WizardStepProps<ActivityWizardData>> = ({
  data,
  setData,
  errors,
}) => {
  const { t } = useTranslation("wizard-activity");
  const { t: tPicker } = useTranslation("media-library-picker");
  const { t: tStakeholder } = useTranslation("stakeholders-picker");
  const theme = useTheme();

  const [stakeholderPickerOpen, setStakeholderPickerOpen] = useState(false);

  const handleStakeholderConfirm = (stakeholder: Stakeholder) => {
    setData({ stakeholders: [...(data.stakeholders || []), stakeholder] });
  };

  const handleStakeholderDelete = (idx: number) => {
    const next = (data.stakeholders || []).slice();
    next.splice(idx, 1);
    setData({ stakeholders: next });
  };

  const excludeUserIds = (data.stakeholders || [])
    .map((s) => s.userId)
    .filter((id): id is number => typeof id === "number");

  const showMaterials = isFieldVisible(
    "didactical_material",
    data.type,
    data.is_standalone,
  );
  const showStakeholders = isFieldVisible(
    "stakeholders",
    data.type,
    data.is_standalone,
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <SectionHeader
        title={t("sections.tools.title")}
        description={t("sections.tools.description") || undefined}
      />

      {showMaterials && (
        <MultiAssociationField
          label={t("fields.didactical-material")}
          values={data.didactical_material}
          onChange={(next) => setData({ didactical_material: next })}
          addLabel={t("common.add") || "Aggiungi"}
          emptyVariant="card"
          mediaType={MediaType.DIDACTICAL_MATERIAL}
          pickerTitle={tPicker("title-material") || ""}
        />
      )}

      <UsefulLinksField
        values={data.useful_links}
        onChange={(next) => setData({ useful_links: next })}
        errors={errors}
      />

      {showStakeholders && (
        <Box>
          <SectionHeader
            title={t("sections.stakeholders.title")}
            description={t("sections.stakeholders.description") || undefined}
          />
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                }}
              >
                {t("fields.stakeholders")}
              </Typography>
              <Box
                onClick={() => setStakeholderPickerOpen(true)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  fontSize: "13px",
                  fontWeight: 600,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {t("common.add") || "Aggiungi"}
                <Plus size={14} />
              </Box>
            </Box>

            {data.stakeholders && data.stakeholders.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {data.stakeholders.map((stakeholder, idx) => (
                  <StakeholdersCstRow
                    key={`stkhldr_${stakeholder.userId}_${idx}`}
                    stakeholder={stakeholder}
                    onDelete={() => handleStakeholderDelete(idx)}
                  />
                ))}
              </Box>
            ) : (
              <EmptyAddCardCst
                addLabel={t("common.add") || "Aggiungi"}
                onAdd={() => setStakeholderPickerOpen(true)}
              />
            )}
          </Box>

          <StakeholdersPickerCst
            open={stakeholderPickerOpen}
            onClose={() => setStakeholderPickerOpen(false)}
            title={tStakeholder("title") || "Aggiungi Stakeholder"}
            excludeUserIds={excludeUserIds}
            onConfirm={handleStakeholderConfirm}
          />
        </Box>
      )}
    </Box>
  );
};

export default ToolsSection;
